import { useState, useEffect } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle, XCircle, Clock, Database, Car, AlertTriangle, Timer } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadSyncStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadSyncStatus(), loadStats(), loadSchedulerStatus()]);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/scheduler/status`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error("Errore caricamento scheduler:", error);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/sync/status`);
      setSyncStatus(response.data);
      if (response.data.status === 'in_progress') {
        setSyncing(true);
      } else {
        setSyncing(false);
      }
    } catch (error) {
      console.error("Errore caricamento stato sync:", error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Errore caricamento statistiche:", error);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API}/sync`);
      toast.success("Sincronizzazione avviata", {
        description: "I dati verranno aggiornati a breve"
      });
      
      const checkStatus = setInterval(async () => {
        const response = await axios.get(`${API}/sync/status`);
        setSyncStatus(response.data);
        
        if (response.data.status === 'success') {
          clearInterval(checkStatus);
          setSyncing(false);
          loadStats();
          toast.success("Sincronizzazione completata", {
            description: `${response.data.vehicles_count} veicoli importati`
          });
        } else if (response.data.status === 'error') {
          clearInterval(checkStatus);
          setSyncing(false);
          toast.error("Errore sincronizzazione", {
            description: response.data.message
          });
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(checkStatus);
        setSyncing(false);
      }, 120000);

    } catch (error) {
      setSyncing(false);
      toast.error("Errore avvio sincronizzazione", {
        description: error.message
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Mai';
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'in_progress':
        return <Clock className="w-6 h-6 text-blue-500 animate-pulse" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'success':
        return 'Completata';
      case 'error':
        return 'Errore';
      case 'in_progress':
        return 'In corso...';
      case 'never_synced':
        return 'Mai sincronizzato';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-page">
      {/* Header */}
      <div className="bg-neutral-950 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-blue-500 font-semibold uppercase tracking-widest text-sm mb-2">
            Gestione
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            PANNELLO ADMIN
          </h1>
          <p className="text-zinc-400 max-w-xl">
            Gestisci la sincronizzazione dei dati dal server FTP
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sync Card */}
          <div className="lg:col-span-2 bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 
                    className="text-xl font-bold text-zinc-900"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    SINCRONIZZAZIONE FTP
                  </h2>
                  <p className="text-sm text-zinc-500">
                    Importa i veicoli dal file XML
                  </p>
                </div>
              </div>
            </div>

            {/* Sync Status */}
            <div className="mb-6">
              <div className={`flex items-center gap-4 p-4 border ${getStatusColor(syncStatus?.status)}`}>
                {getStatusIcon(syncStatus?.status)}
                <div className="flex-1">
                  <p className="font-semibold">
                    {getStatusLabel(syncStatus?.status)}
                  </p>
                  <p className="text-sm opacity-80">
                    {syncStatus?.message || 'Nessuna sincronizzazione eseguita'}
                  </p>
                </div>
                {syncStatus?.timestamp && syncStatus?.status !== 'never_synced' && (
                  <div className="text-right">
                    <p className="text-xs opacity-60">Ultimo aggiornamento</p>
                    <p className="text-sm font-mono">{formatDate(syncStatus.timestamp)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sync Button */}
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 font-bold uppercase tracking-wider py-6 text-base"
              data-testid="sync-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Sincronizzazione in corso...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Avvia Sincronizzazione
                </>
              )}
            </Button>

            {/* FTP Info */}
            <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">
                Fonte dati
              </p>
              <p className="text-sm font-mono text-zinc-700">
                ftp://ftp.promovolkswagen.it/www.promovolkswagen.it/wp-admin/listings/
              </p>
            </div>

            {/* Auto Sync Status */}
            {schedulerStatus && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200">
                <div className="flex items-center gap-3">
                  <Timer className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 uppercase tracking-wider mb-1">
                      Sincronizzazione Automatica
                    </p>
                    <p className="text-sm font-semibold text-green-800">
                      Attiva - Ogni 24 ore
                    </p>
                    {schedulerStatus.jobs?.[0]?.next_run && (
                      <p className="text-xs text-green-600 mt-1">
                        Prossima sync: {formatDate(schedulerStatus.jobs[0].next_run)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stats Card */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 
                  className="text-xl font-bold text-zinc-900"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  STATISTICHE
                </h2>
                <p className="text-sm text-zinc-500">
                  Panoramica del database
                </p>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-20 bg-zinc-100"></div>
                <div className="h-20 bg-zinc-100"></div>
                <div className="h-20 bg-zinc-100"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                    Veicoli Totali
                  </p>
                  <p 
                    className="text-4xl font-bold text-zinc-900"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    data-testid="stat-vehicles"
                  >
                    {stats?.total_vehicles || 0}
                  </p>
                </div>

                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                    Marche
                  </p>
                  <p 
                    className="text-4xl font-bold text-zinc-900"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {stats?.brands_count || 0}
                  </p>
                </div>

                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                    Prezzo Medio
                  </p>
                  <p 
                    className="text-4xl font-bold text-blue-600"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {stats?.avg_price ? `€${Math.round(stats.avg_price / 1000)}K` : '€0'}
                  </p>
                </div>

                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                    KM Medi
                  </p>
                  <p 
                    className="text-4xl font-bold text-zinc-900"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {stats?.avg_mileage ? `${Math.round(stats.avg_mileage / 1000)}K` : '0'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white border border-zinc-200 p-6">
          <h3 
            className="text-lg font-bold text-zinc-900 uppercase tracking-wide mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Come Funziona
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div>
                <p className="font-semibold text-zinc-900 mb-1">Connessione FTP</p>
                <p className="text-sm text-zinc-600">
                  Il sistema si connette al server FTP e scarica il file ZIP con i dati aggiornati.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div>
                <p className="font-semibold text-zinc-900 mb-1">Parsing XML</p>
                <p className="text-sm text-zinc-600">
                  Il file XML viene estratto e analizzato per ottenere i dati dei veicoli.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div>
                <p className="font-semibold text-zinc-900 mb-1">Aggiornamento DB</p>
                <p className="text-sm text-zinc-600">
                  I dati vengono salvati nel database MongoDB, sovrascrivendo i vecchi record.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
